import { CalendarClock, Fingerprint, Mic, Newspaper, Play, Plug } from 'lucide-react'
import type { MissionStep, TourMission } from './tour-state'

const OPEN_MENU = { target: 'toolbar-menu', hint: 'Open the menu' }
const OPEN_SKILLS = { target: 'chat-mentions', hint: 'Open skills' }
const TYPE_AT = { target: 'chat-input', hint: 'Type @ in the chat box' }

const settingsPath = (tab: string, label: string): Array<MissionStep> => [
  { surface: 'overlay', ...OPEN_MENU, skipWhenLaterShows: true },
  { surface: 'overlay', target: 'toolbar-settings', hint: 'Choose Settings', recoverVia: OPEN_MENU },
  { surface: 'settings', target: `settings-tab-${tab}`, hint: `Open ${label}`, skipWhenLaterShows: true },
]

/** The @ menu's row tag, keyed by skill name so a user's own copy of a skill still matches. */
export const mentionTourTarget = (skillName: string) =>
  `mention-skill-${skillName.trim().toLowerCase().replace(/\s+/g, '-')}`

const runSkillPath = (skillName: string, sendHint: string): Array<MissionStep> => [
  { surface: 'overlay', ...OPEN_SKILLS, recoverVia: TYPE_AT, skipWhenLaterShows: true },
  { surface: 'overlay', target: mentionTourTarget(skillName), hint: `Choose ${skillName}`, recoverVia: OPEN_SKILLS },
  { surface: 'overlay', target: 'chat-submit', hint: sendHint, neverSkippedTo: true },
]

/** Defined but not offered. Re-enabling one offers it as a round of its own to users who already finished the tour. */
const PAUSED_MISSIONS: ReadonlySet<string> = new Set(['how-you-work', 'meeting-notes'])

/** In tour order. Ids match the gateway's `TOUR_MISSIONS`. */
const ALL_MISSIONS: ReadonlyArray<TourMission> = [
  {
    id: 'meeting-brief',
    icon: CalendarClock,
    path: ({ meetingId, meetingTitle }) => [
      { surface: 'overlay', target: 'upcoming-view-all', hint: 'Open Upcoming' },
      { surface: 'overlay', target: `meeting-${meetingId}`, hint: `Open ${meetingTitle ?? 'the meeting'}` },
    ],
  },
  {
    id: 'daily-brief',
    icon: Newspaper,
    path: () => [
      { surface: 'overlay', target: 'actions-view-all', hint: 'Open For You', skipWhenLaterShows: true },
      { surface: 'overlay', target: 'daily-brief-card', hint: 'Open your Daily Brief' },
    ],
  },
  {
    id: 'run-skill',
    icon: Play,
    path: ({ skillName }) => runSkillPath(skillName ?? '', 'Send it to run the skill'),
  },
  {
    id: 'integrations',
    icon: Plug,
    path: ({ app, appName }) => [
      ...settingsPath('integrations', 'Integrations'),
      ...(app
        ? [{ surface: 'settings', target: `integration-${app}`, hint: `Connect ${appName}`, optional: true } as const]
        : []),
    ],
  },
  {
    id: 'how-you-work',
    icon: Fingerprint,
    path: () => [
      ...settingsPath('context', 'How You Work'),
      { surface: 'settings', target: 'how-you-work-open', hint: 'See what Highlight knows', skipWhenLaterShows: true },
      {
        surface: 'settings',
        target: 'writing-style',
        hint: 'This is how Highlight writes like you',
        completeOnArrive: true,
      },
    ],
  },
  {
    id: 'meeting-notes',
    icon: Mic,
    path: () => [
      ...settingsPath('meeting-notes', 'Meetings'),
      { surface: 'settings', target: 'meeting-mode-autopilot', hint: 'Choose Autopilot' },
    ],
  },
]

export const TOUR_MISSIONS = ALL_MISSIONS.filter((mission) => !PAUSED_MISSIONS.has(mission.id))
